import { defineStore } from 'pinia';
import { useLocalStorage } from '@vueuse/core';
import type { CVRConfig } from '@/src/types/views';

// Duplicated here to avoid circular import with volume-coloring.ts
const DEFAULT_AMBIENT = 0.2;
const DEFAULT_DIFFUSE = 0.7;
const DEFAULT_SPECULAR = 0.3;

export type VRQualityPreset = 'performance' | 'balanced' | 'cinematic';

export const VR_PRESET_LABELS: Record<VRQualityPreset, string> = {
  performance: 'Performance',
  balanced: 'Balanced',
  cinematic: 'Cinematic',
};

// All presets keep cvr.enabled=true because the "disabled" code path
// activates aggressive gradient opacity filtering that makes the volume
// nearly invisible. The performance difference comes from volumeQuality
// (controls sample distance) and LAO/scatter toggles.
const PRESET_CONFIGS: Record<VRQualityPreset, CVRConfig> = {
  performance: {
    enabled: true,
    lightFollowsCamera: true,
    volumeQuality: 1,
    useVolumetricScatteringBlending: false,
    volumetricScatteringBlending: 0,
    useLocalAmbientOcclusion: false,
    laoKernelSize: 0,
    laoKernelRadius: 0,
    ambient: DEFAULT_AMBIENT,
    diffuse: DEFAULT_DIFFUSE,
    specular: DEFAULT_SPECULAR,
  },
  balanced: {
    enabled: true,
    lightFollowsCamera: true,
    volumeQuality: 2,
    useVolumetricScatteringBlending: false,
    volumetricScatteringBlending: 0,
    useLocalAmbientOcclusion: false,
    laoKernelSize: 0,
    laoKernelRadius: 0,
    ambient: DEFAULT_AMBIENT,
    diffuse: DEFAULT_DIFFUSE,
    specular: DEFAULT_SPECULAR,
  },
  cinematic: {
    enabled: true,
    lightFollowsCamera: true,
    volumeQuality: 3,
    useVolumetricScatteringBlending: false,
    volumetricScatteringBlending: 0.5,
    useLocalAmbientOcclusion: true,
    laoKernelSize: 10,
    laoKernelRadius: 3,
    ambient: DEFAULT_AMBIENT,
    diffuse: DEFAULT_DIFFUSE,
    specular: DEFAULT_SPECULAR,
  },
};

export function getPresetCVRConfig(preset: VRQualityPreset): CVRConfig {
  return { ...PRESET_CONFIGS[preset] };
}

export const useVRQualityStore = defineStore('vrQuality', () => {
  const preset = useLocalStorage<VRQualityPreset>(
    'vr-quality-preset',
    'performance'
  );

  function setPreset(p: VRQualityPreset) {
    preset.value = p;
  }

  return { preset, setPreset };
});
